/** @jsx jsx */
import { css, jsx } from '@emotion/react';

import React, { useRef, useState } from 'react';

import A11yScreenReaderOnly from 'bundles/a11y/components/cds/A11yScreenReaderOnly';
import ModerationReasons from 'bundles/discussions/components/ModerationReasons';
import NotifyOption from 'bundles/discussions/components/NotifyOption';

import _t from 'i18n!nls/discussions';

export type Props = {
  handleCancel: () => void;
  handleSuccess: (selectedOption: string, dontNotify: boolean) => void;
  submitButtonText?: string;
  title?: string;
  subtitle?: string;
  showNotifyUser?: boolean;
};

const ModerationReasonForm = ({
  submitButtonText,
  title,
  subtitle,
  handleCancel,
  handleSuccess,
  showNotifyUser = true,
}: Props) => {
  const [showInputError, setShowInputError] = useState(false);
  const [selectedOption, setSelectedOption] = useState('');
  const [dontNotify, setDontNotify] = useState(false);
  const firstRadioInputRef = useRef<HTMLInputElement>(null);

  const toggleNotify = () => {
    setDontNotify(!dontNotify);
  };

  const handleSelect = (event: React.FormEvent<HTMLInputElement>) => {
    setShowInputError(false);
    setSelectedOption((event.target as HTMLInputElement).value);
  };

  const handleSubmit = () => {
    if (!selectedOption) {
      setShowInputError(true);
      firstRadioInputRef.current?.focus();
    } else {
      handleSuccess(selectedOption, dontNotify);
    }
  };

  return (
    <div data-testid="moderation-reason-form">
      <h1 className="c-modal-title display-1-text">{title || _t('Edit Post')}</h1>
      <div role="group" aria-labelledby="moderation-reason-heading">
        <div className="c-modal-subtitle" id="moderation-reason-subtitle">
          <span data-testid="moderation-reason-subtitle">{subtitle || _t('Why do you want to edit this?')}</span>
        </div>

        <div
          role="alert"
          id="moderation-select-error"
          className="c-error-message"
          css={(theme) =>
            css(`
              margin-bottom: ${theme.spacing(8)};
              color: ${theme.palette.red[700]};
            `)
          }
        >
          {showInputError && (
            <React.Fragment>
              <A11yScreenReaderOnly tagName="span">{_t('Error:')}</A11yScreenReaderOnly>
              <span>{_t('Please select a reason')}</span>
            </React.Fragment>
          )}
        </div>

        <ModerationReasons
          handleSelect={handleSelect}
          ariaLabelledBy="moderation-reason-subtitle"
          ariaDescribedBy="moderation-select-error"
          ref={firstRadioInputRef}
        />
      </div>
      {showNotifyUser && <NotifyOption dontNotify={dontNotify} toggleNotify={toggleNotify} />}
      <div>
        <div className="horizontal-box align-items-right">
          <button className="passive" onClick={handleCancel} type="button">
            {_t('Cancel')}
          </button>
          &nbsp;&nbsp;
          <button className="secondary" type="submit" onClick={handleSubmit}>
            {submitButtonText || _t('Submit')}
          </button>
        </div>
      </div>
    </div>
  );
};

export default ModerationReasonForm;
